import { use } from 'react';
import { Chevron, Info } from '../../../../Icons';
import { DropdownContexts } from '../../context/DropdownContext';
import { SkeletonLoader } from '../../../../Feedback/SkeletonLoader';
import { colors, spacing } from '../../../../../themes';
import { Label } from '../../../../Feedback/Label';
import { type ListDropdownItem } from '../../../../../../../../../packages/types/src/dropdown';
import { DropdownErrorMessage } from './subs/DropdownErrorMessage/DropdownErrorMessage';
import {
  ChevronWrapper,
  DropdownHeaderStyled,
  DropdownHeaderWrapper,
  DropdownLabelStyled,
  DropdownPlaceholderStyled,
  InfoIconWrapper,
  LabelDescriptionWrapper,
  LabelWrapper,
  RequiredIndicator,
  SkeletonWrapper,
  StyledAdditionalSelectedWrapper,
  TopWrapper,
} from './styles';

export const DropdownHeader = () => {
  const { open, error, toggleOpen } = use(DropdownContexts.useDropdown);

  const { hasSelectedItems, dropdownText, additionalSelectedAmount, allSelected } = use(
    DropdownContexts.useValues,
  );
  const {
    disabled,
    label,
    loading,
    required,
    icon,
    showInfoIcon,
    onInfoIconClick,
    testId,
    descriptionOnValue,
  } = use(DropdownContexts.props);
  const selectedItems = use(DropdownContexts.useItems);

  const isPlaceholderActive = !hasSelectedItems;
  const itemDescription = flatMapListDropdownItems(selectedItems.items).find(
    (item) => item.label === dropdownText,
  )?.description;

  return (
    <DropdownHeaderWrapper data-testid="dropdown-header">
      {(label ?? showInfoIcon) && (
        <TopWrapper>
          {label && (
            <LabelWrapper>
              {<DropdownLabelStyled>{label}</DropdownLabelStyled>}
              {required && <RequiredIndicator>*</RequiredIndicator>}
            </LabelWrapper>
          )}

          {showInfoIcon && (
            <InfoIconWrapper onClick={onInfoIconClick}>
              <Info color={colors.primary.blue} cursor={'pointer'} height={'22px'} width={'22px'} />
            </InfoIconWrapper>
          )}
        </TopWrapper>
      )}

      <DropdownHeaderStyled
        disabled={disabled}
        error={error}
        onClick={toggleOpen}
        open={open}
        data-testid={testId}
      >
        {!loading && (
          <>
            <DropdownPlaceholderStyled placholderActive={isPlaceholderActive}>
              {dropdownText}
              {descriptionOnValue && itemDescription && (
                <LabelDescriptionWrapper>{`(${itemDescription})`}</LabelDescriptionWrapper>
              )}
            </DropdownPlaceholderStyled>

            {!allSelected && additionalSelectedAmount > 0 && (
              <StyledAdditionalSelectedWrapper>
                <Label variant="info">{`+${additionalSelectedAmount}`}</Label>
              </StyledAdditionalSelectedWrapper>
            )}
          </>
        )}

        {loading && (
          <SkeletonWrapper>
            <SkeletonLoader height={spacing.small} />
          </SkeletonWrapper>
        )}

        {icon}
        {!icon && (
          <ChevronWrapper>
            <Chevron height="24px" width="24px" rotation={open ? 180 : 0} />
          </ChevronWrapper>
        )}
      </DropdownHeaderStyled>

      <DropdownErrorMessage />
    </DropdownHeaderWrapper>
  );
};

const flatMapListDropdownItems = (
  items: ListDropdownItem[],
): { label: string; description: string }[] => {
  const result: { label: string; description: string }[] = [];

  for (const item of items) {
    if (item.children.length > 0) {
      result.push(...flatMapListDropdownItems(item.children));
    } else {
      result.push({ label: item.label, description: item.description || '' });
    }
  }

  return result;
};
